import React from 'react'
import { cx, dateFormat, dateDiff, dateTimeFormat } from '@/utils/strings'
import ImageContentful from '@/components/ui/image-contentful'
import LinkComponent from '@/components/link-component'
import CtaLink, { buttonStyles } from './ui/cta-link'
import { useTranslations } from '@/utils/locale'

export type ArticleCardProps = {
  description?: string
  image: any
  imageDarkMode?: any
  imageStyle?: 'Icon' | 'Non Icon'
  title: string
  publishDate?: string
  updateDate?: string
  url: string
  cta?: string
  eventDate?: any
  tags?: string[]
  newsfeed?: boolean
  eyebrowText?: string
  attribution?: {
    company: string
    authors?: string[]
  }
}

export const renderTags = (tags: any) => {
  if (!tags || !Array.isArray(tags) || tags.length === 0) return null
  return (
    <div className="mb-4">
      {(tags as unknown as string[]).map((tag, index, array) => (
        <span key={tag} className="text-primaryBlue text-eyebrow inline-block">
          {tag}
          {index < array.length - 1 && (
            <span className="text-[10px] text-secondary-800 dark:text-secondary-50 mx-2">▪</span>
          )}
        </span>
      ))}
    </div>
  )
}

export default function ArticleListCard({
  description,
  image,
  imageDarkMode,
  imageStyle,
  title,
  publishDate,
  updateDate,
  eventDate,
  url,
  cta,
  tags = [],
  newsfeed = false,
  eyebrowText,
  attribution,
}: ArticleCardProps) {
  // Ensure imageStyle defaults to 'Icon' if not provided or if it's an invalid value
  const resolvedImageStyle = imageStyle === 'Non Icon' ? 'Non Icon' : 'Icon'

  const renderEventDate = (event: string) => {
    return (
      <>
        {new Date(event) > new Date() ? (
          <span>{dateFormat(event.toString(), 'en-US')}</span>
        ) : (
          <span>
            {dateTimeFormat(event.toString(), 'en-US')} &nbsp; {dateDiff(event.toString(), 'en-US')}
          </span>
        )}
      </>
    )
  }
  const { t } = useTranslations()
  const editorialPublishDate = updateDate || publishDate
  const publishOrUpdate = updateDate ? t('updated') : t('published')

  return (
    <LinkComponent
      to={`${url}`}
      className={cx(
        'block rounded-lg @container border overflow-hidden',
        'group hover:scale-[1.01] transition-transform duration-300 ease-in-out'
      )}
    >
      <article className={cx('text-left flex flex-col @4xl:flex-row h-full bg-bw-gray-teal dark:bg-secondary-800')}>
        <div className={cx(newsfeed ? 'bg-white @4xl:w-2/12 p-6' : ' @4xl:w-5/12')}>
          <div className="flex h-full">
            {image && image?.fields?.file?.contentType !== 'image/svg+xml' && (
              <div
                className={cx(
                  'h-full w-full overflow-hidden aspect-video flex justify-center items-center',
                  newsfeed ? '@4xl:items-start' : 'rounded-t-lg @4xl:rounded-l-lg @4xl:rounded-t-none '
                )}
              >
                <ImageContentful
                  className={cx(
                    image?.fields?.file?.details?.image?.width &&
                      image?.fields?.file?.details?.image?.height &&
                      image?.fields?.file?.details?.image?.width / image?.fields?.file?.details?.image?.height > 1.2
                      ? 'h-auto'
                      : 'w-auto h-full @4xl:h-auto!',
                    resolvedImageStyle === 'Icon'
                      ? 'h-auto! w-full [&_img]:text-transparent [&_img]:m-auto [&_img]:h-auto [&_img]:w-1/2'
                      : '-mt-[1px]'
                  )}
                  {...image?.fields}
                  darkModeImage={imageDarkMode?.fields}
                />
              </div>
            )}
            {image?.fields?.file?.contentType === 'image/svg+xml' && (
              <div
                className={cx(
                  'flex w-full  aspect-video overflow-hidden items-center',
                  newsfeed
                    ? 'dark:bg-white @4xl:items-start'
                    : 'bg-primaryBlue rounded-t-lg @4xl:rounded-l-lg @4xl:rounded-t-none  '
                )}
              >
                <ImageContentful
                  className={cx(
                    '[&_svg]:text-transparent [&_svg]:m-auto [&_svg]:h-auto [&_svg]:w-1/2 @4xl:[&_svg]:w-full'
                  )}
                  {...image?.fields}
                  darkModeImage={imageDarkMode?.fields}
                />
              </div>
            )}
            {!image && (
              <div className="aspect-video h-full w-full rounded-t-lg @4xl:rounded-l-lg @4xl:rounded-t-none text-center flex items-center justify-center">
                <span className="font-bwi text-[120px] text-primaryBlue">&#xe932;</span>
              </div>
            )}
          </div>
        </div>
        <div
          className={cx(
            'h-full @4xl:h-auto p-6 relative  dark:bg-secondary-800 text-secondary-800 dark:text-secondary-50',
            cta && 'pb-16',
            newsfeed ? '@4xl:w-10/12' : '@4xl:w-7/12'
          )}
        >
          {(eyebrowText || eventDate || editorialPublishDate) && (
            <p className="text-sm mb-2 uppercase text-secondary-600 dark:text-secondary-200">
              {eyebrowText && <>{eyebrowText}</>}
              {eventDate && <time>{renderEventDate(eventDate)}</time>}
              {editorialPublishDate && (
                <time>{`${publishOrUpdate}:  ${dateFormat(editorialPublishDate, 'en-US')}`}</time>
              )}
            </p>
          )}

          {tags && renderTags(tags)}

          {title && <h2 className={cx('text-2xl', !attribution && !description && 'mb-6')}>{title}</h2>}
          {attribution && (
            <div className="flex gap-3 mb-4">
              <div className="bg-primaryBlue text-white dark:text-secondary-900 rounded-md px-2 inline-block">
                {attribution.company}
              </div>
              {attribution.authors && (
                <span className="text-secondary-500 dark:text-secondary-300">{attribution.authors.join(', ')}</span>
              )}
            </div>
          )}
          {description && <p className={cx(!newsfeed && 'line-clamp-3', 'mb-6 text-lg')}>{description}</p>}
          {cta && (
            <div className="absolute bottom-6 right-6 left-6 border-t border-secondary-100">
              <div className="grid justify-items-end pt-6">
                <CtaLink asButton style={buttonStyles.btnArrow} className="text-right">
                  {cta}
                </CtaLink>
              </div>
            </div>
          )}
        </div>
      </article>
    </LinkComponent>
  )
}
